import { type ChangeEvent, useId } from 'react';
import { type Control, type FieldValues, useController } from 'react-hook-form';

import useFormContext from '@src/shared/form/useFormContext';
import Avatar from '@src/shared/ui/Avatar';
import { Button } from '@src/shared/ui/Button';
import { AccountBoxIcon, DeleteIcon, UploadIcon } from '@src/shared/ui/icons';
import { Box } from '@src/shared/ui/layout';
import { FormHelperText } from '@src/shared/ui/form';
import type { SystemStyleObject, Theme } from '@mui/system';

const StyleSheet = {
  root: {
    mb: 2.5,
    display: 'flex',
    alignItems: 'center',
  },
  avatar: {
    backgroundColor: 'grey.200',
    borderRadius: 1,
    mr: 1.25,
    width: 40,
    height: 40,
  },
};

type FormContextShape = {
  control: Control<FieldValues>;
  setValue: (
    name: string,
    value: unknown,
    options?: { shouldDirty?: boolean; shouldValidate?: boolean },
  ) => void;
  watch: (name: string) => unknown;
};

type ImageUploadFieldProps = {
  name: string;
  label: string;
  disabled?: boolean;
  styles?: {
    image?: SystemStyleObject<Theme>;
    root?: SystemStyleObject<Theme>;
  };
  EmptyFiller?: React.ReactNode;
};

function ImageUploadField({ name, label, disabled = false, styles = {}, EmptyFiller}: ImageUploadFieldProps) {
  const { control, setValue, watch } = useFormContext() as FormContextShape;
  const { field, fieldState } = useController({ control, name });
  const helperTextId = useId();
  const previewName = `${name}Preview`;
  const imagePreview = watch(previewName) as string | null;
  const hasError = Boolean(fieldState.error);
  const errorButtonStyles = hasError ? {
    borderColor: 'error.main',
    color: 'error.main',
    '&:hover': {
      borderColor: 'error.main',
    },
  } : undefined;

  const clearImage = () => {
    setValue(name, null, { shouldDirty: true, shouldValidate: true });
    setValue(previewName, null, { shouldDirty: true, shouldValidate: true });
  };

  const handleImageChange = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] ?? null;

    setValue(name, file, { shouldDirty: true, shouldValidate: true });

    if (!file) {
      setValue(previewName, null, { shouldDirty: true, shouldValidate: true });
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      setValue(previewName, typeof reader.result === 'string' ? reader.result : null, {
        shouldDirty: true,
        shouldValidate: true,
      });
    };
    reader.readAsDataURL(file);
  };

  return (
    <Box
      sx={{
        ...(styles.root || StyleSheet.root),
        flexWrap: 'wrap',
      }}
    >
      <Avatar src={imagePreview ?? undefined} sx={styles.image || StyleSheet.avatar}>
        {EmptyFiller ?? <AccountBoxIcon />}
      </Avatar>

      {imagePreview ? (
        <Button
          ref={field.ref}
          variant="outlined"
          onClick={clearImage}
          onBlur={field.onBlur}
          disabled={disabled}
          aria-invalid={hasError || undefined}
          aria-describedby={hasError ? helperTextId : undefined}
          startIcon={<DeleteIcon color="inherit" />}
          sx={errorButtonStyles}
        >
          Delete {label}
        </Button>
      ) : (
        <Button
          ref={field.ref}
          component="label"
          variant="outlined"
          disabled={disabled}
          onBlur={field.onBlur}
          aria-invalid={hasError || undefined}
          aria-describedby={hasError ? helperTextId : undefined}
          startIcon={<UploadIcon color="inherit" />}
          sx={errorButtonStyles}
        >
          Upload {label}
          <input
            hidden
            name={field.name}
            type="file"
            accept="image/*"
            onChange={handleImageChange}
          />
        </Button>
      )}
      {fieldState.error?.message && (
        <FormHelperText
          id={helperTextId}
          error
          sx={{ flexBasis: '100%', ml: 0 }}
        >
          {fieldState.error.message}
        </FormHelperText>
      )}
    </Box>
  );
}

export default ImageUploadField;
